import { Anchor, Breadcrumbs, Text } from '@mantine/core';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import CampaignForm from '../../components/Campaigns/CampaignForm';
import SenderVerificationAlert from '../../components/SenderVerificationAlert';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import { createCampaignFlow } from '../../constants/flowTypes';
import { CampaignTypes, Page } from '../../constants/pageTitles';
import { defaultCampaignGoals, normalizeCampaignGoals } from '../../helpers/campaignGoals';
import { friendlyGoogleErrorMessage } from '../../helpers/googleErrorUtils';
import useFetch from '../../helpers/useFetch';
import useCrossDomainCopyPage from '../../hooks/useCrossDomainCopyPage';

const page = `${Page['campaigns']}`;

const CampaignsNewPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const { from, fromDomain, clearFlowElements } = useCrossDomainCopyPage();

  const [campaign, setCampaign] = useState();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState();
  const { mode, flowElementId, flowType, preselectSegmentId } = queryString.parse(location.search);
  const { domain } = useSelector((state) => state.domain);

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/segments?scope=internal`)
      .then(({ segments }) => {
        setSegments(segments);
      })
      .catch((error) => {
        setLoadError(error);
        setSegments([]);
      });
  }, [fetch, domain.accountId, domain.id]);

  useEffect(() => {
    if (segments === undefined) {
      return;
    }

    if (from) {
      // Use fromDomain if provided (cross-domain copy), otherwise use current domain.id (same domain copy)
      fetch(`/api/admin/accounts/${domain.accountId}/domains/${fromDomain || domain.id}/campaigns/${from}`)
        .then(({ campaign }) => {
          if (!fromDomain) {
            campaign.name = `Copy of ${campaign.name}`;
          }

          campaign.running = false;
          campaign.aborted = false;
          campaign.scheduled = false;
          campaign.googleAssetGroup = campaign.googleAssetGroup || {};
          delete campaign.scheduledAt;
          delete campaign.startedAt;
          delete campaign.finishedAt;
          delete campaign.abortedAt;
          delete campaign.id;

          // Clear selected elements when copying across domains
          const nodeTypes = ['sendEmail', 'sendFacebookAd', 'sendGoogleAd'];
          campaign = clearFlowElements(campaign, nodeTypes);

          setCampaign(normalizeCampaignGoals(campaign));
        })
        .catch(setUserError);
    } else {
      setCampaign({
        name: 'New campaign ' + new Date().toString(),
        type: 'simple',
        mode,
        schedule: undefined,
        scope: 'marketing',
        reentrable: false,
        reentryCooldownInDays: 30,
        scheduled: false,
        trigger: { type: 'event' },
        tags: [],
        useOptimalTime: false,
        flow: createCampaignFlow(flowType, flowElementId),
        googleAssetGroup: {},
        segmentId: preselectSegmentId,
        goals: defaultCampaignGoals(domain),
      });
    }
  }, [
    domain.accountId,
    domain.id,
    fetch,
    flowElementId,
    from,
    mode,
    segments,
    flowType,
    fromDomain,
    clearFlowElements,
    domain,
    preselectSegmentId,
  ]);

  const close = () => {
    navigate(`/accounts/${domain.accountId}/domains/${domain.id}/campaigns?mode=${campaign.mode}`);
  };

  const create = (campaign, cb) => {
    setLoading(true);
    setUserError(undefined);

    return fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/campaigns`, {
      method: 'POST',
      body: JSON.stringify(campaign),
    })
      .then(cb)
      .catch((error) => {
        setUserError({ message: friendlyGoogleErrorMessage(error?.message) });
      })
      .finally(() => setLoading(false));
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (campaign === undefined || segments === undefined) {
    return <Spinner />;
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${domain.accountId}/domains/${domain.id}/analytics` },
    { title: page, href: `#/accounts/${domain.accountId}/domains/${domain.id}/campaigns?mode=${campaign.mode}` },
    { title: 'New' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | ${CampaignTypes[campaign.mode]} | New`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>

      <SenderVerificationAlert />

      <CampaignForm loading={loading} campaign={campaign} saveCampaign={create} close={close} />

      {userError ? <ErrorMessage message={userError.message} /> : null}
    </>
  );
};

export default CampaignsNewPage;
